import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { getAttribution } from "@/lib/attribution";
import { trackFormError, trackFormStart, trackGenerateLead } from "@/lib/tracking";

/** What a lead form collects. Everything but name and email is optional. */
export interface LeadInput {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  /** Human-readable requirement, shown as-is in the CRM. */
  requirement?: string;
  message?: string;
  demo_time?: string;
  /** Machine value: wcag_audit, testing, sebi, vpat, remediation, pdf, sample_report, other. */
  service?: string;
  /** lt_1_month, 1_3_months, 3_6_months, researching. */
  timeline?: string;
  scope?: string;
  /** Landing-page headline variant (?service= value or "default"). */
  page_variant?: string;
}

interface LeadAck {
  status?: string;
  /** Present only when the lead was stored (absent when dropped as spam). */
  id?: string | null;
}

export type SubmitResult = { ok: true; leadId: string | null } | { ok: false };

export const CONTACT_EMAIL = "priya@accessbharat.com";

const blank = (v: string | undefined) => {
  const t = v?.trim();
  return t ? t : undefined;
};

/**
 * Shared submit logic for every public lead form (homepage contact section and
 * the /wcag-audit landing page), so they send identical anti-spam signals and
 * ad attribution and fire conversions under the same rules:
 *
 *  - spam signals: the honeypot (`website`, see <LeadHoneypot>) and how long the
 *    form was open (`form_elapsed_ms`); without them the server drops the lead;
 *  - attribution: gclid / UTMs / landing page from the `ab_attrib` cookie;
 *  - `generate_lead` (Google Ads / GA4) and Umami `lead_submitted` fire only
 *    when the API returns the stored lead's id — never on errors or on a
 *    submission the server dropped as spam;
 *  - `form_error` on API failure, with an inline message carrying our email.
 */
export function useLeadSubmit(formId: string) {
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const honeypotRef = useRef<HTMLInputElement>(null);
  const openedAtRef = useRef<number | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    openedAtRef.current = Date.now();
  }, []);

  /** Call on the first input in the form; fires `form_start` once. */
  const markStarted = useCallback(
    (service?: string) => {
      if (startedRef.current) return;
      startedRef.current = true;
      trackFormStart(formId, service);
    },
    [formId],
  );

  const submit = useCallback(
    async (input: LeadInput): Promise<SubmitResult> => {
      setSubmitError("");
      const tenantId = import.meta.env.VITE_PUBLIC_TENANT_ID;
      if (!tenantId) {
        setSubmitError(
          `The contact form isn't available right now. Please email us at ${CONTACT_EMAIL}.`,
        );
        trackFormError(formId, "config");
        return { ok: false };
      }

      setLoading(true);
      const a = getAttribution();
      try {
        const ack = await api.post<LeadAck | undefined>(
          `/leads/public?tenant_id=${encodeURIComponent(tenantId)}`,
          {
            name: input.name.trim(),
            email: input.email.trim(),
            message: blank(input.message),
            phone: blank(input.phone),
            company: blank(input.company),
            requirement: blank(input.requirement),
            demo_time: input.demo_time,
            service: blank(input.service),
            timeline: blank(input.timeline),
            scope: blank(input.scope),
            page_variant: blank(input.page_variant),
            gclid: a.gclid,
            gbraid: a.gbraid,
            wbraid: a.wbraid,
            utm_source: a.utm_source,
            utm_medium: a.utm_medium,
            utm_campaign: a.utm_campaign,
            utm_term: a.utm_term,
            utm_content: a.utm_content,
            matchtype: a.matchtype,
            first_touch_ts: a.first_touch_ts,
            landing_page:
              a.landing_page ?? (typeof location !== "undefined" ? location.pathname : undefined),
            website: honeypotRef.current?.value ?? "",
            form_elapsed_ms: openedAtRef.current === null ? 0 : Date.now() - openedAtRef.current,
          },
        );
        const leadId = ack?.id ?? null;
        if (leadId) {
          trackGenerateLead({
            formId,
            leadId,
            email: input.email,
            phone: input.phone,
            leadService: blank(input.service),
            leadTimeline: blank(input.timeline),
            pageVariant: blank(input.page_variant),
          });
          // Umami (cookieless). Category only — no PII.
          trackEvent("lead_submitted", { requirement: blank(input.requirement) });
        }
        return { ok: true, leadId };
      } catch (err) {
        setSubmitError(`Something went wrong. Please try again or email us at ${CONTACT_EMAIL}.`);
        // fetch() rejects with a TypeError when the request never got a reply.
        trackFormError(formId, err instanceof TypeError ? "network" : "server");
        return { ok: false };
      } finally {
        setLoading(false);
      }
    },
    [formId],
  );

  return { honeypotRef, loading, submitError, markStarted, submit };
}
